// frontend/src/components/Samples/SampleOrderList.jsx

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { sampleOrdersAPI, customersAPI, productionTypesAPI, sampleOrderTypesAPI, usersAPI } from '../../api/client';
import { useTranslation } from '../../context/TranslationContext';
import { FiPlus, FiEye, FiX } from 'react-icons/fi';
import SearchableSelect from '../Common/SearchableSelect';
import DataTable from '../Common/DataTable';
import Pagination from '../Common/Pagination';
import './SampleOrderList.css';
import { getOrderStatusClass, ORDER_STATUS_META } from '../../constants/statusMeta';


// ── Main list ─────────────────────────────────────────────────────────────────
const SampleOrderList = () => {
  const { t } = useTranslation();
  const navigate      = useNavigate();
  const routeLocation = useLocation();

  // Restore state: SampleOrderForm navigates back with flat returnState
  const _rs = routeLocation.state?.returnState || routeLocation.state || {};

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(_rs.currentPage || 1);
  const [itemsPerPage, setItemsPerPage] = useState(_rs.itemsPerPage || 25);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  const [filters, setFilters] = useState(_rs.filters || {
    sampleOrderNo: '', customerId: '', status: '',
    productionTypeId: '', sampleOrderTypeId: '', createdBy: '',
  });
  const [orderNoInput, setOrderNoInput] = useState(_rs.filters?.sampleOrderNo || '');

  // Ref-based debounce — no extra useEffect, no extra renders
  const orderNoDebounce = useRef(null);

  const [customers, setCustomers] = useState([]);
  const [productionTypes, setProductionTypes] = useState([]);
  const [sampleOrderTypes, setSampleOrderTypes] = useState([]);
  const [users, setUsers] = useState([]);

  useEffect(() => {
    Promise.all([
      customersAPI.getAll(),
      productionTypesAPI.getAll(),
      sampleOrderTypesAPI.getAll(),
      usersAPI.getAll(),
    ]).then(([cr, pr, sr, ur]) => {
      if (cr.data.success) setCustomers(cr.data.data);
      if (pr.data.success) setProductionTypes(pr.data.data);
      if (sr.data.success) setSampleOrderTypes(sr.data.data);
      if (ur.data.success) setUsers(ur.data.users || []);
    }).catch(console.error);

  }, []);

  
  useEffect(() => { loadOrders(); }, [currentPage, itemsPerPage, filters]);

  const loadOrders = async () => {
    try {
      setLoading(true);
      const q = new URLSearchParams({
        page: currentPage, limit: itemsPerPage,
        ...(filters.sampleOrderNo    && { sampleOrderNo:    filters.sampleOrderNo }),
        ...(filters.status           && { status:           filters.status }),
        ...(filters.customerId       && { customerId:       filters.customerId }),
        ...(filters.productionTypeId && { productionTypeId: filters.productionTypeId }),
        ...(filters.sampleOrderTypeId&& { sampleOrderTypeId:filters.sampleOrderTypeId }),
        ...(filters.createdBy        && { createdBy:        filters.createdBy }),
      });
      const res = await sampleOrdersAPI.getAll(q.toString());
      if (res.data.success) {
        setOrders(res.data.data);
        setTotalPages(res.data.pagination.totalPages);
        setTotalItems(res.data.pagination.totalItems);
      }
      setError(null);
    } catch (err) {
      setError(t('messages.error_load', 'Failed to load sample orders'));
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setCurrentPage(1);
  };


  const priorityLabel = (p) => ({
    1: t('priority.urgent', 'Urgente'),
    2: t('priority.normal', 'Normal'),
    3: t('priority.low', 'Baixa'),
  }[p] ?? (p ?? '—'));

  const columns = [
    { header: t('sample_orders.order_no', 'Order No'),         field: 'sample_order_no',         className: 'code-column', width: '100px', minWidth: '90px' },
    { header: t('sample_orders.customer', 'Customer'),          field: 'customer_name',           width: '160px', minWidth: '120px' },
    { header: t('sample_orders.subject', 'Assunto'),            field: 'subject',                 width: '200px', minWidth: '140px',
      render: (row) => row.subject || '—' },
    { header: t('sample_orders.external_doc', 'External Doc'),  field: 'external_document',       width: '110px', minWidth: '90px' },
    { header: t('sample_orders.production_type', 'Prod. Type'), field: 'production_type',         width: '100px', minWidth: '90px',
      render: (row) => row.production_type || '—' },
    { header: t('sample_orders.order_type', 'Order Type'),      field: 'sample_order_type',       width: '90px', minWidth: '90px',
      render: (row) => row.sample_order_type || '—' },
    { header: t('sample_orders.created_by', 'Criado Por'),      field: 'full_name',               width: '120px', minWidth: '100px',
      render: (row) => row.full_name || '—' },
    { header: t('sample_orders.order_date', 'Order Date'),      field: 'order_date',              width: '80px',  minWidth: '80px',
      render: (row) => row.order_date ? new Date(row.order_date).toLocaleDateString() : '—' },
    { header: t('sample_orders.delivery_date', 'Delivery'),     field: 'requested_delivery_date', width: '80px',  minWidth: '80px',
      render: (row) => row.requested_delivery_date ? new Date(row.requested_delivery_date).toLocaleDateString() : '—' },
    { header: t('sample_orders.priority', 'Priority'),          field: 'priority', className: 'text-center', width: '80px', minWidth: '70px',
      render: (row) => priorityLabel(row.priority) },
    { header: t('sample_orders.status', 'Status'), field: 'status', sortable: false, width: '120px', minWidth: '100px',
      render: (row) => (
        <span className={`status-badge ${getOrderStatusClass(row.status)}`}>
          {ORDER_STATUS_META[row.status]?.label || row.status}
        </span>
      )
    },
/*    
    { header: t('sample_orders.description', 'Description'),  field: 'sample_order_description',
      render: (row) => (
        <span className="desc-cell" title={row.sample_order_description}>
          {row.sample_order_description || '—'}
        </span>
      )
    },
*/    
    { header: t('actions', 'Actions'), field: 'actions', sortable: false,
      render: (row) => (
        <div className="action-buttons">
          <button onClick={(e) => { e.stopPropagation(); navigate(`/samples/orders/${row.public_id}`, {
              state: { returnTo: '/samples/orders', returnState: { filters, currentPage, itemsPerPage } },
        }); }}
            className="btn-action btn-view" title={t('view', 'View')}>
            <FiEye size={16} />
          </button>
        </div>
      )
    }
  ];

  return (
    <div className="sample-order-list">
      <div className="page-header page-header--compact">
        <h2>{t('sample_orders.title', 'Pedidos de Amostra')}</h2>
        <div className="page-header-actions">
          <button onClick={() => navigate('/samples/orders/new')} className="btn-primary">
            <FiPlus size={16} style={{ marginRight: '6px' }} />
            {t('sample_orders.create', 'Criar Pedido de Amostra')}
          </button>
        </div>
      </div>

      <div className="order-filters">
        <div className="filters-row">

          {/* Order No */}
          <div className="filter-group filter-group-sm">
            <label>{t('sample_orders.order_no', 'Order No')}</label>
            <div className="filter-search-wrap">
              <input type="text" className="filter-search-input"
                value={orderNoInput}
                onChange={e => {
                  const val = e.target.value;
                  setOrderNoInput(val);
                  if (orderNoDebounce.current) clearTimeout(orderNoDebounce.current);
                  orderNoDebounce.current = setTimeout(() => {
                    handleFilterChange('sampleOrderNo', val);
                  }, 350);
                }}
                placeholder={t('search', 'Search...')} />
              {orderNoInput && (
                <span className="filter-search-clear"
                  onClick={() => {
                    setOrderNoInput('');
                    if (orderNoDebounce.current) clearTimeout(orderNoDebounce.current);
                    handleFilterChange('sampleOrderNo', '');
                  }}>
                  <FiX size={13} />
                </span>
              )}
            </div>
          </div>

          {/* Customer — searchable */}
          <div className="filter-group filter-group-lg">
            <label>{t('sample_orders.customer', 'Customer')}</label>
            <SearchableSelect
              value={filters.customerId}
              onChange={v => handleFilterChange('customerId', v)}
              options={customers.map(c => ({ value: c.customer_id, code: c.customer_code, name: c.customer_name }))}
              getOptionLabel={opt => `${opt.code} — ${opt.name}`}
              getOptionValue={opt => opt.value}
              placeholder={t('all', 'All')}
            />
          </div>

          {/* Production Type */}
          <div className="filter-group filter-group-sm">
            <label>{t('sample_orders.production_type', 'Production Type')}</label>
            <select value={filters.productionTypeId}
              onChange={e => handleFilterChange('productionTypeId', e.target.value)}>
              <option value="">{t('all', 'All')}</option>
              {productionTypes.map(pt => (
                <option key={pt.production_type_id} value={pt.production_type_id}>
                  {pt.production_type}
                </option>
              ))}
            </select>
          </div>

          {/* Sample Order Type */}
          <div className="filter-group filter-group-sm">
            <label>{t('sample_orders.order_type', 'Order Type')}</label>
            <select value={filters.sampleOrderTypeId}
              onChange={e => handleFilterChange('sampleOrderTypeId', e.target.value)}>
              <option value="">{t('all', 'All')}</option>
              {sampleOrderTypes.map(sot => (
                <option key={sot.sample_order_type_id} value={sot.sample_order_type_id}>
                  {sot.sample_order_type}
                </option>
              ))}
            </select>
          </div>

          {/* Status */}
          <div className="filter-group filter-group-sm">
            <label>{t('sample_orders.status', 'Status')}</label>
            <select value={filters.status}
              onChange={e => handleFilterChange('status', e.target.value)}>
              <option value="">{t('all', 'All')}</option>
              <option value="OPEN">{t('status.open', 'Aberto')}</option>
              <option value="RELEASED">{t('status.released', 'Liberto')}</option>
              <option value="IN PROGRESS">{t('status.in_progress', 'Em Curso')}</option>
              <option value="SENT CUSTOMER">{t('status.sent_customer', 'Enviado Cliente')}</option>
              <option value="CLOSED">{t('status.closed', 'Fechado')}</option>
              <option value="CANCELLED">{t('status.cancelled', 'Cancelado')}</option>
            </select>
          </div>

          {/* Created By */}
          <div className="filter-group filter-group-md">
            <label>{t('sample_orders.created_by', 'Criado Por')}</label>
            <SearchableSelect
              value={filters.createdBy}
              onChange={v => handleFilterChange('createdBy', v)}
              options={users.map(u => ({ value: u.user_id, label: u.full_name }))}
              getOptionLabel={opt => opt.label}
              getOptionValue={opt => opt.value}
              placeholder={t('all', 'All')}
            />
          </div>

        </div>
      </div>

      <DataTable columns={columns} data={orders} loading={loading} error={error}
        onRetry={loadOrders}
        onRowClick={(row) => navigate(`/samples/orders/${row.public_id}`, {
          state: { returnTo: '/samples/orders', returnState: { filters, currentPage, itemsPerPage } },
        })}
        emptyMessage={t('no_data', 'No sample orders found')} />

      {totalPages > 1 && (
        <Pagination currentPage={currentPage} totalPages={totalPages}
          totalItems={totalItems} itemsPerPage={itemsPerPage}
          onPageChange={setCurrentPage}
          onItemsPerPageChange={(v) => { setItemsPerPage(v); setCurrentPage(1); }} />
      )}
    </div>
  );
};

export default SampleOrderList;